// scanengine servlet's V3 carpet API wire format types (private — matches the scanengine V3 carpet JSON structure)

interface SuccessResponse {
  success: true;
  message: string;
  lastUpdated: string;
  title: string;
  totalSectors: number;
  totalSymbols: number;
  totalTradingDays: number;
  sectors: Sector[];
}

interface ErrorResponse {
  success: false;
  message: string;
  reason: string;
}

type Response = SuccessResponse | ErrorResponse;

interface Sector {
  name: string;
  count: number;
  symbols: SymbolInformation[];
}

interface SymbolInformation {
  symbol: string;
  name: string | null;
  marketCap: number | null;
  startingMovingAverages: {
    rsiFourteenDayEmaGain: number | null;
    rsiFourteenDayEmaLoss: number | null;
    ppoTwelveDayEma: number | null;
    ppoTwentySixDayEma: number | null;
  };
  dailyData: DailyDataPoint[];
}

interface DailyDataPoint {
  date: string;
  closePrice: number | null;
  sctr: number | null;
}

// Internal types — what the rest of the application works with

export interface MarketCarpetDataResponse {
  title: string;
  sectors: SectorData[];
  dates: string[];
  lastUpdated: string;
}

export interface SectorData {
  name: string;
  tickers: Ticker[];
}

export interface Ticker {
  symbol: string;
  name: string;
  marketCap: number | null;
  sctr: (number | null)[];
  prices: number[];
  startingMovingAverages: {
      ppoTwelveDayEma: number;
      ppoTwentySixDayEma: number;
      rsiFourteenDayEmaLoss: number;
      rsiFourteenDayEmaGain: number;
  }
}

// Typed error carrying the scanengine servlet's V3 carpet API's
// machine-readable reason code so callers can distinguish
// transient failures from permanent ones.
export class ApiError extends Error {
  readonly reason: string;
  constructor(message: string, reason: string) {
    super(message);
    this.reason = reason;
  }
}

const SCAN_ENGINE_BASE_URL = '/servlet/ScanEngine.CScanServlet?scan=';

const TITLE_MAP = {
  'Dow 30 Industrial Stocks': 'Dow Jones Industrial Stocks',
  'Dow 20 Transportation Stocks': 'Dow Jones Transportation Stocks',
  'Dow 15 Utility Stocks': 'Dow Jones Utility Stocks'
};

function isSuccessResponse(json: unknown): json is SuccessResponse {
  if (typeof json !== 'object' || json === null)
    return false;
  return (
    'success' in json &&
    json.success === true &&
    'sectors' in json &&
    Array.isArray(json.sectors) &&
    'lastUpdated' in json &&
    typeof json.lastUpdated === 'string' &&
    'title' in json &&
    typeof json.title === 'string'
  );
}

function transformResponse(successResponse: SuccessResponse): MarketCarpetDataResponse {
  const firstSymbol = successResponse.sectors[0]?.symbols[0];
  const dates = firstSymbol?.dailyData.map(d => d.date) ?? [];

  return {
    title: successResponse.title,
    lastUpdated: successResponse.lastUpdated,
    dates,
    sectors: successResponse.sectors.map(sector => ({
      name: sector.name in TITLE_MAP
        ? TITLE_MAP[sector.name as keyof typeof TITLE_MAP]
        : sector.name,
      tickers: sector.symbols.map(sym => ({
        symbol: sym.symbol,
        name: sym.name || sym.symbol,
        marketCap: sym.marketCap,
        // scanengine servlet's V3 carpet API returns null for rare NaN values
        // in closePrice and EMA seeds. Coercing to 0 may distort intermediate
        // calculations for that ticker, but Calculator.getCalculations()
        // catches non-finite final results.
        prices: sym.dailyData.map(d => d.closePrice ?? 0),
        sctr: sym.dailyData.map(d => d.sctr),
        startingMovingAverages: {
          rsiFourteenDayEmaGain: sym.startingMovingAverages.rsiFourteenDayEmaGain ?? 0,
          rsiFourteenDayEmaLoss: sym.startingMovingAverages.rsiFourteenDayEmaLoss ?? 0,
          ppoTwelveDayEma: sym.startingMovingAverages.ppoTwelveDayEma ?? 0,
          ppoTwentySixDayEma: sym.startingMovingAverages.ppoTwentySixDayEma ?? 0
        }
      }))
    }))
  };
}

export async function fetchMarketCarpetData(scanCommand: string): Promise<MarketCarpetDataResponse> {
  const RESPONSE_TIME_OUT = 30000;
  const controller = new AbortController();
  setTimeout(() => controller.abort(), RESPONSE_TIME_OUT);

  const response = await fetch(SCAN_ENGINE_BASE_URL + scanCommand, {
      signal: controller.signal,
      cache: 'reload'
  });
  if (!response.ok)
    throw new Error(`Server error (HTTP ${response.status})`);
  const json: Response = await response.json();

  if (!isSuccessResponse(json)) {
    if (
      typeof json === 'object' &&
      json !== null &&
      'message' in json &&
      typeof json.message === 'string' &&
      'reason' in json &&
      typeof json.reason === 'string'
    ) {
      throw new ApiError(json.message, json.reason);
    }
    throw new Error('Unknown API error');
  }

  const result = transformResponse(json);

  // ================ TODO: Remove this workaround once the database is fixed ================
  // VTI (Vanguard Total Stock Market ETF) is a US domestic ETF that does NOT belong in
  // the International ETFs group. It's incorrectly included because VTI is a member of
  // the PINTLETF predefined group in the database. The "International ETFs" report
  // template (scanengine: src/main/resources/reports/reportTemplates/internationalETFs.json)
  // uses scan expression "I.N|G.0|USAL[G.PINTLETF]" which pulls all members of PINTLETF.
  //
  // The correct fix is to remove VTI from the PINTLETF predefined group in the database
  // (loaded via stored procedure getGroupMembersSE('PINTLETF')). Once that DB change is
  // made, this front-end filter should be removed.
  if (result.title === 'International ETFs' && result.sectors[0]) {
    result.sectors[0].tickers = result.sectors[0].tickers.filter((t) => t.symbol !== 'VTI');
  }
  // =========================================================================================

  return result;
}
